import React, { useState, useEffect, useCallback } from 'react';
import { adminAlertAPI } from '../../services/api';
import * as Icons from 'lucide-react';

const AlertManagementAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [configs, setConfigs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [search, setSearch] = useState('');

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const fetchStats = useCallback(async () => {
    try { const res = await adminAlertAPI.getStats(); setStats(res.data || {}); } catch (e) { console.error(e); }
  }, []);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try { const res = await adminAlertAPI.getAllConfigs(); setConfigs(res.data?.configs || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try { const res = await adminAlertAPI.getSchedules(); setSchedules(res.data?.schedules || []); } catch (e) { console.error(e); }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try { const res = await adminAlertAPI.getHistory({ limit: 200 }); setHistory(res.data?.history || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    if (activeTab === 'users') fetchConfigs();
    if (activeTab === 'schedules') fetchSchedules();
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, fetchConfigs, fetchSchedules, fetchHistory]);

  const tabs = [
    { key: 'overview', label: 'Overview', icon: Icons.LayoutDashboard },
    { key: 'users', label: 'User Configs', icon: Icons.Users },
    { key: 'schedules', label: 'Schedules', icon: Icons.Clock },
    { key: 'history', label: 'Alert History', icon: Icons.History },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 bg-blue-100 rounded-xl"><Icons.BellRing className="w-6 h-6 text-blue-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alert Management</h1>
          <p className="text-sm text-gray-500">Configure and monitor alerts for all users</p>
        </div>
      </div>

      {msg.text && (
        <div className={`mb-4 p-3 rounded-lg border text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {msg.type === 'success' ? <Icons.CheckCircle className="w-4 h-4" /> : <Icons.AlertCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total Configs', value: stats.total_configs || 0, color: 'blue', icon: Icons.Settings },
          { label: 'Low Balance', value: stats.enabled_low_balance || 0, color: 'amber', icon: Icons.Wallet },
          { label: 'Login Alerts', value: stats.enabled_login || 0, color: 'orange', icon: Icons.ShieldAlert },
          { label: 'SMS Summary', value: stats.enabled_sms_summary || 0, color: 'green', icon: Icons.BarChart3 },
          { label: 'Sent Today', value: stats.today_sent || 0, color: 'purple', icon: Icons.Send },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase">{s.label}</p>
                <p className={`text-2xl font-bold mt-1 text-${s.color}-600`}>{s.value}</p>
              </div>
              <div className={`p-2 bg-${s.color}-50 rounded-lg`}><s.icon className={`w-5 h-5 text-${s.color}-500`} /></div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-200">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.key ? 'border-blue-500 text-blue-600 bg-blue-50/50' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeTab === 'overview' && <OverviewTab stats={stats} />}
          {activeTab === 'users' && <UsersTab configs={configs} loading={loading} search={search} setSearch={setSearch} showMsg={showMsg} onRefresh={fetchConfigs} />}
          {activeTab === 'schedules' && <SchedulesTab schedules={schedules} showMsg={showMsg} onRefresh={fetchSchedules} />}
          {activeTab === 'history' && <HistoryTab history={history} loading={loading} />}
        </div>
      </div>
    </div>
  );
};

const OverviewTab = ({ stats }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-green-50 border border-green-200 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-3"><Icons.CheckCircle className="w-5 h-5 text-green-600" /><h4 className="font-semibold text-green-800">Sent</h4></div>
        <p className="text-3xl font-bold text-green-700">{stats.total_sent || 0}</p>
        <p className="text-xs text-green-600 mt-1">Today: {stats.today_sent || 0}</p>
      </div>
      <div className="bg-red-50 border border-red-200 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-3"><Icons.XCircle className="w-5 h-5 text-red-600" /><h4 className="font-semibold text-red-800">Failed</h4></div>
        <p className="text-3xl font-bold text-red-700">{stats.total_failed || 0}</p>
        <p className="text-xs text-red-600 mt-1">Today: {stats.today_failed || 0}</p>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-3"><Icons.Clock className="w-5 h-5 text-amber-600" /><h4 className="font-semibold text-amber-800">Pending</h4></div>
        <p className="text-3xl font-bold text-amber-700">{stats.total_pending || 0}</p>
      </div>
    </div>
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
      <h4 className="font-semibold text-gray-800 mb-3">Alert Types Summary</h4>
      <div className="grid grid-cols-3 gap-4 text-center">
        <div><p className="text-2xl font-bold text-amber-600">{stats.enabled_low_balance || 0}</p><p className="text-xs text-gray-500">Low Balance Alerts</p></div>
        <div><p className="text-2xl font-bold text-orange-600">{stats.enabled_login || 0}</p><p className="text-xs text-gray-500">Login Alerts</p></div>
        <div><p className="text-2xl font-bold text-green-600">{stats.enabled_sms_summary || 0}</p><p className="text-xs text-gray-500">SMS Summary Alerts</p></div>
      </div>
    </div>
  </div>
);

const UsersTab = ({ configs, loading, search, setSearch, showMsg, onRefresh }) => {
  const filtered = configs.filter(c =>
    !search || c.username?.toLowerCase().includes(search.toLowerCase()) || c.full_name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">All users with alert configurations</p>
        <div className="relative w-64">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>

      {loading ? <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div> : filtered.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><Icons.Users className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No alert configs found</p></div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="bg-gray-50">
            {['User', 'Low Balance', 'Login Alert', 'SMS Summary', 'Email', 'SMS', 'Updated'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>{filtered.map(c => (
            <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50/50">
              <td className="px-4 py-2.5"><div className="text-xs font-medium text-gray-800">{c.username}</div><div className="text-[10px] text-gray-400">{c.full_name}</div></td>
              <td className="px-4 py-2.5">{c.low_balance_enabled ? <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-50 text-green-700 border border-green-200">ON</span> : <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-50 text-gray-500 border border-gray-200">OFF</span>}</td>
              <td className="px-4 py-2.5">{c.login_alert_enabled ? <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-50 text-green-700 border border-green-200">ON</span> : <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-50 text-gray-500 border border-gray-200">OFF</span>}</td>
              <td className="px-4 py-2.5">{c.sms_summary_enabled ? <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-50 text-green-700 border border-green-200">{c.sms_summary_frequency?.toUpperCase()}</span> : <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-50 text-gray-500 border border-gray-200">OFF</span>}</td>
              <td className="px-4 py-2.5">{c.email_enabled ? <Icons.Check className="w-4 h-4 text-green-500" /> : <Icons.X className="w-4 h-4 text-gray-300" />}</td>
              <td className="px-4 py-2.5">{c.sms_enabled ? <Icons.Check className="w-4 h-4 text-green-500" /> : <Icons.X className="w-4 h-4 text-gray-300" />}</td>
              <td className="px-4 py-2.5 text-xs text-gray-500">{c.updated_at ? new Date(c.updated_at).toLocaleDateString() : '-'}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
};

const SchedulesTab = ({ schedules, showMsg, onRefresh }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const handleSave = async () => {
    if (!editing) return;
    try {
      await adminAlertAPI.updateSchedule(editing, form);
      showMsg('success', 'Schedule updated');
      setEditing(null);
      onRefresh();
    } catch (e) { showMsg('error', e.response?.data?.error || 'Failed to update'); }
  };

  const formatInterval = (seconds) => {
    if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const alertTypeLabels = {
    low_balance: 'Low Balance Alert',
    login_alert: 'Login Alert',
    sms_summary_daily: 'SMS Summary (Daily)',
    sms_summary_weekly: 'SMS Summary (Weekly)',
    sms_summary_monthly: 'SMS Summary (Monthly)',
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Configure timing and batch size for alert processing. When enabled, alerts are sent one-by-one per the configured interval.</p>

      <div className="grid grid-cols-1 gap-4">
        {schedules.map(s => (
          <div key={s.id} className={`border rounded-xl p-5 ${s.is_enabled ? 'bg-blue-50/50 border-blue-200' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${s.is_enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <Icons.Clock className={`w-5 h-5 ${s.is_enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{alertTypeLabels[s.alert_type] || s.alert_type}</h4>
                  <p className="text-xs text-gray-500">Interval: {formatInterval(s.interval_seconds)} | Batch: {s.batch_size} per run</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${s.is_enabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                  {s.is_enabled ? 'ENABLED' : 'DISABLED'}
                </span>
                <button onClick={() => { setEditing(s.id); setForm({ is_enabled: s.is_enabled, interval_seconds: s.interval_seconds, batch_size: s.batch_size }); }}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Icons.Settings className="w-4 h-4" /></button>
              </div>
            </div>

            {editing === s.id && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <select value={form.is_enabled ? 'true' : 'false'} onChange={e => setForm(f => ({ ...f, is_enabled: e.target.value === 'true' }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Interval (seconds)</label>
                    <input type="number" min="1" value={form.interval_seconds} onChange={e => setForm(f => ({ ...f, interval_seconds: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                    <p className="text-[10px] text-gray-400 mt-1">60 = 1 min, 3600 = 1 hour</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Batch Size</label>
                    <input type="number" min="1" max="1000" value={form.batch_size} onChange={e => setForm(f => ({ ...f, batch_size: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                    <p className="text-[10px] text-gray-400 mt-1">Emails per batch run</p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                  <button onClick={handleSave} className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Save</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const HistoryTab = ({ history, loading }) => {
  const statusBadge = (status) => {
    const cfg = { sent: 'bg-green-50 text-green-700 border-green-200', failed: 'bg-red-50 text-red-700 border-red-200', pending: 'bg-amber-50 text-amber-700 border-amber-200' };
    return <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${cfg[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{status?.toUpperCase()}</span>;
  };

  return (
    <div className="space-y-4">
      {loading ? <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div> : history.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><Icons.History className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No alert history yet</p></div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="bg-gray-50">
            {['Time', 'User', 'Type', 'Channel', 'Recipient', 'Status', 'Error'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>{history.map(h => (
            <tr key={h.id} className="border-t border-gray-100 hover:bg-gray-50/50">
              <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</td>
              <td className="px-4 py-2.5 text-xs font-medium text-gray-800">{h.username || `#${h.user_id}`}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{h.alert_type?.replace(/_/g, ' ')}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{h.channel}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600 max-w-[150px] truncate">{h.recipient}</td>
              <td className="px-4 py-2.5">{statusBadge(h.status)}</td>
              <td className="px-4 py-2.5 text-xs text-red-500 max-w-[200px] truncate" title={h.error_message}>{h.error_message || '-'}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
};

export default AlertManagementAdmin;
